import { useLayoutEffect, useState, type RefObject } from 'react';
import { PREVIEW_MAX, previewMaxHeight } from '../lib/composerModel';

/**
 * Предел высоты превью этапа 1 на телефоне (ТЗ главной § 5, § 7): от видимой области
 * (`visualViewport`), а не от окна — с открытой клавиатурой iPhone окно прежнее, а видно меньше.
 * Меряем при открытии, при изменении окна и видимой области и когда полоса сдвинулась над
 * клавиатурой (`shift` — её `translateY`, она применяется уже после события resize).
 */
export function usePreviewRoom(open: boolean, row: RefObject<HTMLElement>, shift: number, below = false): number {
  const [room, setRoom] = useState(PREVIEW_MAX);

  useLayoutEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const measure = () => {
      const el = row.current;
      if (!el) return;
      const viewTop = vv ? vv.offsetTop : 0;
      const rect = el.getBoundingClientRect();
      // Строка сверху — превью раскрывается вниз: место — от её низа до низа видимой области (над клавиатурой).
      const viewBottom = viewTop + (vv ? vv.height : window.innerHeight);
      const next = previewMaxHeight(below ? viewBottom - rect.bottom : rect.top - viewTop);
      setRoom((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
    };
  }, [open, row, shift, below]);

  return room;
}

/** Высота видимой области: лист модулей на телефоне не выше неё (клавиатура поиска не закрывает лист). */
export function useVisibleHeight(active: boolean): number {
  const [height, setHeight] = useState(() =>
    typeof window === 'undefined' ? 800 : (window.visualViewport?.height ?? window.innerHeight),
  );
  useLayoutEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    const update = () => setHeight(vv?.height ?? window.innerHeight);
    update();
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
    };
  }, [active]);
  return height;
}
